import { Box, Button, IconButton, Modal, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useHttp } from "../../hooks/useHttp"; // Import SCSS styles
import { Image } from "../../models/images";
import { Pipeline, Task, TaskBatch } from "../../models/tasks";
import "./CreateTaskModal.scss";
import HelpIcon from "@mui/icons-material/Help";
import { HelpType } from "../Help/Help";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

declare module "react" {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string;
  }
}

interface CreateTaskModalProps {
  open: boolean;
  handleClose: () => void;
  setTasks: Function;
  preselectedImageId?: string;
  setHelp: React.Dispatch<React.SetStateAction<HelpType>>;
  isBatch: boolean;
}

export const CreateTaskModal = ({
  open,
  handleClose,
  setTasks,
  preselectedImageId,
  setHelp,
  isBatch,
}: CreateTaskModalProps) => {
  const [taskName, setTaskName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [images, setImages] = useState<Image[]>([]);
  const [selectedImages, setSelectedImages] = useState<Image[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline>(Pipeline.LUNGS_REPORT);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { trigger: fetchImages } = useHttp<Image[]>("/images");
  const { trigger: createLungsTaskTrigger } = useHttp<Task>("/lungsTasks");
  const { trigger: createNodulesTaskTrigger } = useHttp<Task>("/nodulesTasks");
  const { trigger: createBatchTaskTrigger } = useHttp<TaskBatch>("/batch");

  useEffect(() => {
    const cachedImages = sessionStorage.getItem("cachedImages");
    if (cachedImages) {
      setImages(JSON.parse(cachedImages));
    } else {
      fetchImages({
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          sessionId: sessionStorage.getItem("sessionId") || "",
        },
      }).then((response) => {
        if (response) {
          setImages(response);
          sessionStorage.setItem("cachedImages", JSON.stringify(response));
        }
      });
    }
  }, [fetchImages]);

  useEffect(() => {
    if (preselectedImageId) {
      const preselectedImage = images.find((image: Image) => image.id === preselectedImageId);
      if (preselectedImage) {
        setSelectedImages([preselectedImage]);
      }
    }
  }, [preselectedImageId, images]);

  const handleCreateBatchTask = () => {
    if (selectedImages.length === 0) {
      return;
    }
    const req = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        name: taskName,
        description: description,
        lungsImageIds: selectedPipeline === Pipeline.LUNGS_REPORT ? selectedImages.map((img) => img.id) : [],
        nodulesImageIds: selectedPipeline === Pipeline.NODULES ? selectedImages.map((img) => img.id) : [],
        pipeline: selectedPipeline,
      },
    };

    createBatchTaskTrigger(req).then((response: TaskBatch) => {
      if (response) {
        setTasks((prevState: any) => [...prevState, response]);
        setTaskName("");
        setTimeout(() => {
          navigate(`/batch?id=${response.id}`);
          handleClose();
        }, 300);
      }
    });
  };

  // Function to format bytes to human-readable format
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleCreateTask = async () => {
    let response: Task;

    if (selectedFiles && selectedFiles.length > 0) {
      const formData = new FormData();
      const bodyData = {
        name: taskName,
        description: description,
      };

      formData.append("body", new Blob([JSON.stringify(bodyData)], { type: "application/json" }));

      const directoryName = selectedFiles[0].webkitRelativePath.split("/")[0];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        if (file.name.toLowerCase().endsWith(".dcm")) {
          const relativePath = file.webkitRelativePath.substring(directoryName.length + 1);
          formData.append("files", file, relativePath);
        }
      }

      const req = {
        method: "POST",
        headers: {},
        data: formData,
      };

      if (selectedPipeline === Pipeline.LUNGS_REPORT) response = await createLungsTaskTrigger(req);
      else if (selectedPipeline === Pipeline.NODULES) response = await createNodulesTaskTrigger(req);
      else throw new Error("Invalid pipeline");
    } else {
      // Use existing image
      const req = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          name: taskName,
          imageId: selectedImages[0].id,
          description: description,
        },
      };

      if (selectedPipeline === Pipeline.LUNGS_REPORT) response = await createLungsTaskTrigger(req);
      else if (selectedPipeline === Pipeline.NODULES) response = await createNodulesTaskTrigger(req);
      else if (selectedPipeline === Pipeline.BATCH) response = await createBatchTaskTrigger(req);
      else throw new Error("Invalid pipeline");
    }

    if (response) {
      setTasks((prevState: any) => [...prevState, response]);
      setTaskName("");
      setSelectedFiles(null);
      setTimeout(() => {
        if (selectedPipeline === Pipeline.BATCH) {
          navigate(`/batch?id=${response.id}`);
        } else {
          navigate(`/tasks?id=${response.id}`);
        }
        handleClose();
      }, 300);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="createTaskModal">
        <Typography variant="h6" component="h2" gutterBottom>
          {isBatch ? "Create New Batch Task" : "Create New Task"}
        </Typography>
        <IconButton
          className="createTaskModal-help"
          onClick={() => {
            setHelp(HelpType.CREATE_TASK);
          }}
        >
          <HelpIcon />
        </IconButton>
        <TextField
          className="createTaskModal-input"
          fullWidth
          label="Type task name"
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          margin="normal"
        />
        <TextField
          className="createTaskModal-input createTaskModal-input-description"
          fullWidth
          label="Type description or notes"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
        />
        <Box>
          <Typography variant="h6" component="h2" gutterBottom>
            Images
          </Typography>
          <Box className="createTaskModal-images">
            <UploadTile
              selected={selectedFiles !== null}
              onSelect={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
              onFileSelected={(files) => {
                setSelectedFiles(files);
                setSelectedImages([]);
              }}
              fileInputRef={fileInputRef}
              formatBytes={formatBytes}
            />
            {images.map((image) => (
              <ImageComponent
                key={image.id}
                image={image}
                selected={selectedImages.some((img) => img.id === image.id)}
                select={() => {
                  if (selectedFiles) {
                    setSelectedFiles(null);
                  }

                  if (isBatch) {
                    if (selectedImages.some((img) => img.id === image.id)) {
                      setSelectedImages(selectedImages.filter((img) => img.id !== image.id));
                    } else {
                      setSelectedImages([...selectedImages, image]);
                    }
                  } else {
                    setSelectedImages([image]);
                  }
                }}
              />
            ))}
          </Box>
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-start" className="button-stack">
          <Button
            variant={selectedPipeline === Pipeline.LUNGS_REPORT ? "contained" : "outlined"}
            color="primary"
            onClick={() => setSelectedPipeline(Pipeline.LUNGS_REPORT)}
          >
            Lungs Report
          </Button>
          <Button
            variant={selectedPipeline === Pipeline.NODULES ? "contained" : "outlined"}
            color="primary"
            onClick={() => setSelectedPipeline(Pipeline.NODULES)}
          >
            Nodules
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="flex-end" className="button-stack">
          <Button onClick={handleClose} className="createTaskModal-close" variant="contained" color="secondary">
            Cancel
          </Button>
          <Button
            onClick={isBatch ? handleCreateBatchTask : handleCreateTask}
            className="createTaskModal-send"
            variant="contained"
            color="primary"
            disabled={!taskName || (selectedImages.length === 0 && selectedFiles === null)}
          >
            Create
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

interface ImageComponentProps {
  image: Image;
  selected?: boolean;
  select: () => void;
}

const ImageComponent = ({ image, selected, select }: ImageComponentProps) => {
  return (
    <Box
      className={selected ? "createTaskModal-image createTaskModal-image-selected" : "createTaskModal-image"}
      onClick={select}
    >
      <Box className="createTaskModal-image-text">
        <Typography variant="body1" component="p">
          {image.name}
        </Typography>
        <Typography variant="body2" component="p">
          {image.description}
        </Typography>
      </Box>
      <img src={image.base64Image} alt={image.name} />
    </Box>
  );
};

interface UploadTileProps {
  selected: boolean;
  onSelect: () => void;
  onFileSelected: (files: FileList) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  formatBytes: (bytes: number, decimals?: number) => string;
}

const UploadTile = ({ selected, onSelect, onFileSelected, fileInputRef, formatBytes }: UploadTileProps) => {
  const [fileInfo, setFileInfo] = useState<{
    totalFiles: number;
    validFiles: number;
    directoryName: string;
    totalSizeBytes: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Validate that all files are .dcm
      let validFiles = 0;
      let invalidFiles = 0;
      let directoryName = "";
      let totalSizeBytes = 0;

      // Extract directory name from the first file's path
      if (files[0].webkitRelativePath) {
        const pathParts = files[0].webkitRelativePath.split("/");
        if (pathParts.length > 0) {
          directoryName = pathParts[0];
        }
      }

      // Count valid and invalid files and calculate total size
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.name.toLowerCase().endsWith(".dcm")) {
          validFiles++;
          totalSizeBytes += file.size;
        } else {
          invalidFiles++;
        }
      }

      // Set file info
      setFileInfo({
        totalFiles: files.length,
        validFiles: validFiles,
        directoryName: directoryName,
        totalSizeBytes: totalSizeBytes,
      });

      // Set error if there are invalid files
      if (invalidFiles > 0) {
        setError(`Warning: ${invalidFiles} file(s) are not .dcm files and will be ignored.`);
      } else {
        setError(null);
      }

      // Only pass valid files to the parent component
      if (validFiles > 0) {
        onFileSelected(files);
      }
    }
  };

  return (
    <Box
      className={
        selected
          ? "createTaskModal-image createTaskModal-image-selected createTaskModal-upload-tile createTaskModal-upload-tile-selected"
          : "createTaskModal-image createTaskModal-upload-tile"
      }
      onClick={onSelect}
    >
      <input
        type="file"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        webkitdirectory=""
      />
      <Box className="createTaskModal-image-text">
        <Typography variant="body1" component="p">
          Upload DICOM Directory
        </Typography>
        {selected && fileInfo ? (
          <>
            <Typography variant="body2" component="p" sx={{ fontWeight: "bold" }}>
              Directory: {fileInfo.directoryName}
            </Typography>
            <Typography variant="body2" component="p">
              {fileInfo.validFiles} valid DICOM files found ({formatBytes(fileInfo.totalSizeBytes)})
            </Typography>
            {error && (
              <Typography variant="body2" component="p" color="error">
                {error}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" component="p">
            Click to select a directory with DICOM files
          </Typography>
        )}
      </Box>
      <Box className="createTaskModal-image-upload-icon">
        <CloudUploadIcon fontSize="large" />
      </Box>
    </Box>
  );
};
